import type { ReactNode } from 'react'

/**
 * El carril de un Gantt: la rejilla, la línea de «ahora» y lo que se pinte
 * encima.
 *
 * Eran cuarenta líneas dentro de `GanttRow`, y el cuadro de muelles necesita
 * exactamente lo mismo con otras barras dentro. Se extrae **sin cambiar un
 * píxel**: los mismos colores, el mismo grosor y el mismo orden de capas.
 *
 * Lo que va encima llega por `children`, posicionado en absoluto con los `%` que
 * da `track.pos`. Este componente no sabe qué son esas barras, y ésa es la idea.
 */
export function CarrilTrack({
  boundaries,
  todayX,
  dark,
  children,
  className = 'flex-1 relative min-w-0 overflow-hidden py-2 px-1',
  style,
  ahoraDiscontinua = false,
}: {
  /** Líneas de rejilla, en % del ancho. */
  boundaries: number[]
  /** Posición de «ahora» en %, o `null` si cae fuera del rango visible. */
  todayX: number | null
  dark: boolean
  children?: ReactNode
  className?: string
  style?: React.CSSProperties
  /** «Ahora» está dentro del rango pero fuera de la jornada del muelle: la línea
   *  se pinta igual, en el borde del bloque y punteada. Esconderla haría creer
   *  que hoy no está en pantalla. */
  ahoraDiscontinua?: boolean
}) {
  return (
    <div className={className} style={style}>
      {/* Rejilla: días en semanal, semanas en mensual, meses en anual, horas en
          el cuadro de muelles. */}
      {boundaries.map((p, i) => (
        <div
          key={i}
          className="absolute top-0 bottom-0 w-px pointer-events-none"
          style={{ left: `${p}%`, backgroundColor: dark ? 'rgba(255,255,255,0.10)' : 'rgba(0,0,0,0.10)' }}
        />
      ))}

      {/* Línea vertical de hoy */}
      {todayX !== null && (
        ahoraDiscontinua ? (
          <div
            className="absolute top-0 bottom-0 pointer-events-none z-10"
            style={{
              left: `${todayX}%`,
              width: 0,
              borderLeft: `1.5px dashed ${dark ? 'rgba(248,113,113,0.55)' : 'rgba(239,68,68,0.40)'}`,
            }}
          />
        ) : (
          <div
            className="absolute top-0 bottom-0 pointer-events-none z-10"
            style={{
              left: `${todayX}%`,
              width: 1.5,
              backgroundColor: dark ? 'rgba(248,113,113,0.55)' : 'rgba(239,68,68,0.40)',
            }}
          />
        )
      )}

      {children}
    </div>
  )
}
